import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { HStack } from '~/components/elements/HStack'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryCustomerAttributeGroups } from '~/hooks/useSurveyQuery'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useSortCustomerAttributeGroupsMutation } from '../hooks/useSortCustomerAttributeGroupsMutation'
import { useSurveyId } from '../hooks/useSurveyId'

import styles from './SortCustomerAttributeGroupDialog.css'

export const SortCustomerAttributeGroupDialog = () => {
  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()

  const [sortedGroups, setSortedGroups] = React.useState(
    customerAttributeGroups,
  )

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const sortCustomerAttributeGroupsMutation =
    useSortCustomerAttributeGroupsMutation({
      onSuccess: (data) => {
        // 対象サーベイが見つからない場合などupdateCustomerAttributeGroupOrderIndexesはresult: falseを返す。並び替えできていないので記録しない。
        if (data.updateCustomerAttributeGroupOrderIndexes?.result) {
          logEvent('Sort customer attribute group')
        }
        closeDialogWithRefresh()
      },
    })

  const handleMoveUp = React.useCallback<
    (index: number) => EH<StyledButtonProps, 'onClick'>
  >(
    (index) => () => {
      setSortedGroups(R.swapIndices(index - 1, index))
    },
    [],
  )

  const handleMoveDown = React.useCallback<
    (index: number) => EH<StyledButtonProps, 'onClick'>
  >(
    (index) => () => {
      setSortedGroups(R.swapIndices(index, index + 1))
    },
    [],
  )

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyId = useSurveyId()
  const handleExecute = React.useCallback(() => {
    sortCustomerAttributeGroupsMutation.mutate({
      surveyId,
      input: sortedGroups.map((group, index) => ({
        id: group.id,
        orderIndex: index,
      })),
    })
  }, [sortCustomerAttributeGroupsMutation, surveyId, sortedGroups])

  if (dialogInfo?.dialogType !== 'sort') {
    throw new Error()
  }

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const { t } = useTranslation()

  return (
    <ExecuteCancelDialog
      disabledExecute={false}
      executing={sortCustomerAttributeGroupsMutation.isPending}
      mode="settings"
      title={t('sort_customized_segments_group')}
      onCancel={resetDialogInfo}
      onExecute={handleExecute}
    >
      <VStack spacing={2}>
        {sortedGroups.map((group, index) => (
          <HStack key={group.id} align="center" spacing={2}>
            <div className={styles.itemName}>{group.name}</div>

            <StyledButton
              buttonStyle="normal"
              disabled={index === 0}
              icon="arrow-up"
              onClick={handleMoveUp(index)}
            >
              {t('sort_up')}
            </StyledButton>

            <StyledButton
              buttonStyle="normal"
              disabled={index === sortedGroups.length - 1}
              icon="arrow-down"
              onClick={handleMoveDown(index)}
            >
              {t('sort_down')}
            </StyledButton>
          </HStack>
        ))}
      </VStack>
    </ExecuteCancelDialog>
  )
}
